

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { User, Mail, Phone, Edit3, Check, X } from "lucide-react";
import userAPI from "../../api/UserApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import { containerVariants, itemVariants } from "./variants";

// Same fields/flow as sanwariyaLatest/src/pages/user/Account.js:
// firstName, lastName, email (read-only), phone (mobile)
// validate() -> userAPI.updateDetails(payload) -> res.code === 200 -> toast
function Account({ user, loadingUser, refreshUser }) {
  const [formData, setFormData] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [editingField, setEditingField] = useState(null); // "name" | "phone" | null
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.mobile || user.phone || "",
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName) {
      newErrors.firstName = "Please Enter First Name";
    }
    if (!formData.phone) {
      newErrors.phone = "Please Enter Phone Number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const res = await userAPI.updateDetails(formData);
      if (res.code === 200) {
        showSuccess(res.message);
        setEditingField(null);
        refreshUser?.();
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phone: user?.mobile || user?.phone || "",
    });
    setErrors({});
    setEditingField(null);
  };

  const fullName = [formData.firstName, formData.lastName].filter(Boolean).join(" ") || "—";

  return (
    <motion.div
      key="settings"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0, scale: 0.97 }}
      className="flex flex-col gap-3.5"
    >
      <div className="mb-1">
        <span className="block mb-0.5 font-subheading text-[0.54rem] font-bold uppercase tracking-[0.2em] text-[var(--color-text-muted)]">
          Manage
        </span>
        <h2 className="font-heading text-[1.7rem] leading-none tracking-[-0.02em] text-[var(--color-text)]">
          Account Details
        </h2>
      </div>

      <div className="acc-two-col grid grid-cols-1">
        <motion.div variants={itemVariants} className="acc-card px-[18px] py-4">
          <span className="acc-eye">Personal Details</span>

          {/* Full Name — editable, wired to userAPI.updateDetails */}
          <div className="acc-set-row">
            <div className="flex flex-1 min-w-0 items-center gap-2.5">
              <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded bg-[rgba(166,71,46,0.08)]">
                <User size={14} className="text-[var(--color-primary)]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="mb-px font-body text-[0.5rem] uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
                  Full Name
                </p>
                {editingField === "name" ? (
                  <div className="flex gap-1.5">
                    <input
                      className="tf-field-input tf-input w-[48%] rounded-[3px] border border-[var(--color-border)] px-1.5 py-1 font-body text-[0.9rem]"
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="First name"
                    />
                    <input
                      className="tf-field-input tf-input w-[48%] rounded-[3px] border border-[var(--color-border)] px-1.5 py-1 font-body text-[0.9rem]"
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Last name"
                    />
                  </div>
                ) : (
                  <p className="overflow-hidden text-ellipsis whitespace-nowrap font-body text-[0.9rem] font-semibold text-[var(--color-text)]">
                    {loadingUser ? "…" : fullName}
                  </p>
                )}
                {errors.firstName && (
                  <span className="text-danger text-[0.5rem]">{errors.firstName}</span>
                )}
              </div>
            </div>
            {editingField === "name" ? (
              <div className="flex flex-shrink-0 gap-1.5">
                <Check
                  size={14}
                  className={`text-[var(--color-accent-green)] ${saving ? "cursor-not-allowed opacity-50" : "cursor-pointer opacity-100"}`}
                  onClick={saving ? undefined : handleSave}
                />
                <X size={14} className="cursor-pointer text-[var(--color-text-muted)]" onClick={handleCancelEdit} />
              </div>
            ) : (
              <Edit3
                size={13}
                className="flex-shrink-0 cursor-pointer text-[var(--color-text-muted)] opacity-55"
                onClick={() => setEditingField("name")}
              />
            )}
          </div>

          {/* Email — read-only, cannot be changed */}
          <div className="acc-set-row cursor-default">
            <div className="flex flex-1 min-w-0 items-center gap-2.5">
              <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded bg-[rgba(166,71,46,0.08)]">
                <Mail size={14} className="text-[var(--color-primary)]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="mb-px font-body text-[0.5rem] uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
                  Email Address
                </p>
                <p className="overflow-hidden text-ellipsis whitespace-nowrap font-body text-[0.9rem] font-semibold text-[var(--color-text)]">
                  {loadingUser ? "…" : (formData.email || "—")}
                </p>
              </div>
            </div>
          </div>

          {/* Phone — editable, wired to userAPI.updateDetails */}
          <div className="acc-set-row">
            <div className="flex flex-1 min-w-0 items-center gap-2.5">
              <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded bg-[rgba(166,71,46,0.08)]">
                <Phone size={14} className="text-[var(--color-primary)]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="mb-px font-body text-[0.5rem] uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
                  Phone Number
                </p>
                {editingField === "phone" ? (
                  <input
                    className="tf-field-input tf-input w-full rounded-[3px] border border-[var(--color-border)] px-1.5 py-1 font-body text-[0.9rem]"
                    type="text"
                    name="phone"
                    maxLength={10}
                    value={formData.phone}
                    onChange={handleChange}
                  />
                ) : (
                  <p className="overflow-hidden text-ellipsis whitespace-nowrap font-body text-[0.9rem] font-semibold text-[var(--color-text)]">
                    {loadingUser ? "…" : (formData.phone || "No phone on file")}
                  </p>
                )}
                {errors.phone && (
                  <span className="text-danger text-[0.5rem]">{errors.phone}</span>
                )}
              </div>
            </div>
            {editingField === "phone" ? (
              <div className="flex flex-shrink-0 gap-1.5">
                <Check
                  size={14}
                  className={`text-[var(--color-accent-green)] ${saving ? "cursor-not-allowed opacity-50" : "cursor-pointer opacity-100"}`}
                  onClick={saving ? undefined : handleSave}
                />
                <X size={14} className="cursor-pointer text-[var(--color-text-muted)]" onClick={handleCancelEdit} />
              </div>
            ) : (
              <Edit3
                size={13}
                className="flex-shrink-0 cursor-pointer text-[var(--color-text-muted)] opacity-55"
                onClick={() => setEditingField("phone")}
              />
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default Account;
